import { PricePointType, type PricingApiBundle } from '@/api/pricing';
import { isCrosslistingBundle } from '@/helpers/listingUtils';
import { BundlePeriod, type BundleTier, type SelectedBundle } from '@/types';

interface PriceInfo {
  initialPrice: number;
  recurringPrice: number;
  recurringWeeklyPrice: number;
  oldPrice?: number;
}

export function mapPricingBundleToPriceInfo(bundle: PricingApiBundle): PriceInfo {
  const initialPricePoint = bundle.price_points.find(
    (pricePoint) => pricePoint.type === PricePointType.INITIAL,
  )!;
  const recurringPricePoint = bundle.price_points.find(
    (pricePoint) => pricePoint.type === PricePointType.RECURRING,
  )!;
  return {
    oldPrice: recurringPricePoint.old_price,
    initialPrice: initialPricePoint.price,
    recurringPrice: recurringPricePoint.price,
    recurringWeeklyPrice: recurringPricePoint.pricePerWeek ?? recurringPricePoint.price,
  };
}

export function mapPricingBundleToSelectedBundle(bundle: PricingApiBundle): SelectedBundle {
  const priceInfo = mapPricingBundleToPriceInfo(bundle);
  return {
    id: bundle.bundle_id,
    tier: bundle.name,
    period: bundle.period,
    initialPrice: priceInfo.initialPrice,
    recurringPrice: priceInfo.recurringPrice,
    recurringWeeklyPrice: priceInfo.recurringWeeklyPrice,
    oldPrice: priceInfo.oldPrice,
  };
}

// Calculate the difference in price between 2 bundles
// Will always return positive delta
export function getRecurringWeeklyPriceSurcharge(
  baseBundle: PricingApiBundle,
  targetBundle: PricingApiBundle,
): number {
  const basePriceInfo = mapPricingBundleToPriceInfo(baseBundle);
  const targetPriceInfo = mapPricingBundleToPriceInfo(targetBundle);

  return Math.max(0, targetPriceInfo.recurringWeeklyPrice - basePriceInfo.recurringWeeklyPrice);
}

export enum BundleSegment {
  T2F = 't2f',
  PRIVATE = 'private',
}

export function getBundleSegment(bundleId: string): BundleSegment | undefined {
  if (bundleId.includes('T2T-F-')) {
    return BundleSegment.T2F;
  }

  if (bundleId.includes('BUNDLE_PRIVATE-')) {
    return BundleSegment.PRIVATE;
  }

  return undefined;
}

function matchesBundleSegment(bundleId: string, segment: BundleSegment): boolean {
  return getBundleSegment(bundleId) === segment;
}

export function findMatchingBundle(
  bundles: PricingApiBundle[],
  tier: BundleTier,
  period: BundlePeriod = BundlePeriod.FOUR_WEEKS,
  isCrosslisting: boolean,
  segment?: BundleSegment,
): PricingApiBundle | undefined {
  return bundles.find(
    (bundle) =>
      bundle.name === tier &&
      //Period is returned as number in the API, but we define it as enum
      //so we need to compare with == instead of === to avoid false negatives
      bundle.period == period &&
      (!isCrosslisting || isCrosslistingBundle(bundle.bundle_id)) &&
      (segment === undefined || matchesBundleSegment(bundle.bundle_id, segment)),
  );
}
